import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import BarChart from '../BarChart';
import { apiGet } from '../../services/_common';
import SkeletonTable from '../SkeletonTable';
import IssueGuide from '../IssueGuide';
import { IUser } from '../../atoms';
import { useQuery } from '@tanstack/react-query';
import styled from 'styled-components';

const Root = styled.div`
  height: 100%;

  .widget-header {
    padding: 1.5rem 1rem 0 1rem;
    > .widget-title {
      > span {
        color: var(--widget-text-primary);
        font-size: var(--widget-font-size-title);
      }
    }
  }

  .widget-body {
    padding: 1rem;
    width: 100%;
    height: 100%;
  }

  .infoWrapper {
    display: flex;
    gap: 1rem;
    justify-content: flex-end;
    width: 100%;
    > div {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      > div:nth-child(1) {
        font-size: var(--widget-font-size-xs);
        color: var(--widget-text-secondary);
      }
      > div:nth-child(2) {
        font-size: var(--widget-font-size-2xl);
        font-weight: var(--font-bold);
      }

      .theme-blue {
        color: var(--widget-primary);
      }
      .theme-navy {
        color: var(--widget-theme-navy);
      }
    }

    .legend-item {
      display: flex;
      align-items: center;
      gap: 0.5rem;

      .legend-color {
        width: 12px;
        height: 12px;
        border-radius: 2px;
        display: flex;
        align-items: center;
        justify-content: center;

        &.theme-blue {
          border: 1px solid var(--outline);
          background-color: var(--widget-primary);
        }

        &.theme-navy {
          border: 1px solid var(--outline);
          background-color: var(--widget-theme-navy);
        }
      }
    }
  }

  .barChartWrapper {
    width: 100%;
    height: calc(100% - 60px); /* infoWrapper 높이만큼 빼기 */
    min-height: 200px;
  }

  .skeletonWrapper {
    width: 100%;
    height: 100%;
  }
`;

// 주간 출역 그래프
export const WeekAttendGraph = ({ uniqueKey, userInfo, refetchInterval }: { uniqueKey: string; userInfo: IUser; refetchInterval?: number }) => {
  const isSiteDashboard = refetchInterval !== undefined; // 상황판여부
  const { hCd, sCd } = userInfo;
  const { t } = useTranslation();
  const [attendBarData, setAttendBarData] = useState<any[]>([]);
  const [formattedDates, setFormattedDates] = useState<string[]>([]);

  const {
    data: newWorkerData,
    isLoading: isLoadingNewWorkerData,
    isError: isErrorNewWorkerData,
  } = useQuery(['TotalInfoNewWorkerGet', uniqueKey, userInfo], () => fetchNewWorkerData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }),
  });

  const {
    data: attendData,
    isLoading: isLoadingAttendData,
    isError: isErrorAttendData,
  } = useQuery(['attendCntGet', userInfo], () => fetchAttendData(), {
    enabled: !!hCd && !!sCd && sCd !== '00000',
    ...(isSiteDashboard && { refetchInterval }),
  });

  // 공통 날짜 범위 계산 함수
  const getDateRange = () => {
    const today = dayjs();
    const sevenDaysAgo = today.subtract(7, 'day');
    return {
      startDate: sevenDaysAgo.format('YYYYMMDD'),
      endDate: today.format('YYYYMMDD'),
    };
  };

  // 공통 날짜별 그룹핑 함수
  const groupByDate = (dataList: any[], dateField: string) => {
    const grouped = dataList.reduce((acc: { [key: string]: number }, item: any) => {
      const date = item[dateField];
      if (date) {
        acc[date] = (acc[date] || 0) + 1;
      }
      return acc;
    }, {});

    return Object.entries(grouped)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, count]) => ({ date, count }));
  };

  // 오늘로부터 7일간 신규 근로자 인원 수 조회
  const fetchNewWorkerData = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const req = { wWorkstatus: 'I', wJobdate1: startDate, wJobdate2: endDate, hCd, sCd };
      const res = await apiGet({ path: '/worker', req });

      if (res.status === 200) {
        const { workerList } = res.data.data;
        const dailyNewWorkerCount = groupByDate(workerList, 'wJobdate');

        return {
          newWorkerCnt: workerList.length,
          dailyNewWorkerCount,
        };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // 오늘로부터 7일간 출역 요약 조회
  const fetchAttendData = async () => {
    try {
      const { startDate, endDate } = getDateRange();
      const req = { aDate1: startDate, aDate2: endDate, hCd, sCd };
      const res = await apiGet({ path: '/attend/day/summary', req });

      if (res.status === 200) {
        const { dailyCounts = [] } = res.data.data || {};
        // sjCd별로 분리된 집계를 날짜 기준으로 합산
        const byDate: Record<string, number> = {};
        for (const d of dailyCounts) {
          byDate[d.aDate] = (byDate[d.aDate] || 0) + d.cnt;
        }
        const dailyAttendCount = Object.entries(byDate)
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([date, count]) => ({ date, count }));
        const todayAttendCnt = Object.values(byDate).reduce((s, c) => s + c, 0);
        return { todayAttendCnt, dailyAttendCount };
      }
      return undefined;
    } catch (error) {
      console.error(error);
      throw new Error('error');
    }
  };

  // BarChart용 데이터 생성
  useEffect(() => {
    if (newWorkerData?.dailyNewWorkerCount && attendData?.dailyAttendCount) {
      // 모든 날짜를 수집하여 정렬
      const allDates = new Set([...newWorkerData.dailyNewWorkerCount.map((item: any) => item.date), ...attendData.dailyAttendCount.map((item: any) => item.date)]);
      const sortedDates = Array.from(allDates).sort();

      // 각 날짜별로 데이터 매핑
      const newWorkerCounts = sortedDates.map((date) => {
        const found = newWorkerData.dailyNewWorkerCount.find((item: any) => item.date === date);
        return found ? found.count : 0;
      });

      const attendCounts = sortedDates.map((date) => {
        const found = attendData.dailyAttendCount.find((item: any) => item.date === date);
        return found ? found.count : 0;
      });

      // 날짜를 MM/DD(weekday) 형식으로 변환
      const formattedDatesArray = sortedDates.map((date) => {
        const dayjsDate = dayjs(date);
        const weekday = dayjsDate.format('ddd'); // 월, 화, 수, 목, 금, 토, 일
        return `${dayjsDate.format('MM/DD')}(${weekday})`;
      });

      // CSS 변수에서 색상 값 가져오기
      const navyColor = getComputedStyle(document.documentElement).getPropertyValue('--widget-theme-navy').trim();

      // BarChart 데이터 형식으로 변환
      const barData = [
        {
          label: t('신규 근로자'),
          data: newWorkerCounts,
          backgroundColor: 'rgba(79, 107, 232, 1)',
          borderColor: 'rgba(79, 107, 232, 1)',
          borderWidth: 1,
        },
        {
          label: t('전체 출역'),
          data: attendCounts,
          backgroundColor: navyColor,
          borderColor: navyColor,
          borderWidth: 1,
        },
      ];

      setAttendBarData(barData);
      setFormattedDates(formattedDatesArray);
    }
  }, [newWorkerData, attendData, t]);

  const loading = !newWorkerData || !attendData || isLoadingNewWorkerData || isLoadingAttendData;
  if (isErrorNewWorkerData || isErrorAttendData) return <IssueGuide />;

  return (
    <Root>
      <div className='widget-header'>
        <div className='widget-title flex-between'>
          <span>{t('주간 출역 그래프')}</span>
        </div>
      </div>
      {loading ? (
        <div className='skeletonWrapper'>
          <SkeletonTable />
        </div>
      ) : null}
      <div className='widget-body'>
        <div className='infoWrapper'>
          <div className='flex-between'>
            <div className='legend-item'>
              <div className='legend-color theme-blue' />
              <span>{t('신규 근로자')}</span>
            </div>
            <div className='theme-blue'>{newWorkerData?.newWorkerCnt ?? 0}</div>
          </div>
          <div className='flex-between'>
            <div className='legend-item'>
              <div className='legend-color theme-navy' />
              <span>{t('전체 출역')}</span>
            </div>
            <div className='theme-navy'>{attendData?.todayAttendCnt ?? 0}</div>
          </div>
        </div>
        <div className='barChartWrapper'>
          <BarChart labelsParam={formattedDates} barData={attendBarData} showGrid={{ x: false, y: false }} showLegend={false} />
        </div>
      </div>
    </Root>
  );
};
